import { IStockIncompleteFormStockItemsPostBody } from "@/api_framework/api_modals/Inventory";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import Input from "@/app/components/ui/Inputs/Input";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useStockItemsPostAPI } from "@/app/hooks/api_hooks/Inventory/StockIncompleteForm/useStockItemsPostAPI";
import { useOrderManagementClinicListAPI } from "@/app/hooks/api_hooks/orders/useOrderClinicListAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import StockItemListSearch from "./StockItemListSearch";

export interface IInventoryStockItems {
  item_name_sub: string;
  other: string;
  quantity: number;
  category: string;
}
export interface IInventoryStockIncompleteFormProps {
  requester_name: string;
  client_name: string;
  client_id: string;
  clinics: string;
  items: IInventoryStockItems[];
  remarks: string;
  date: string;
}

export interface VendorSectionSearchResultType {
  item_name: string;
}

const CategoryList: ISelectMenuItemData[] = [
  {
    title: "Food",
    id: "food",
  },
  {
    title: "Medicine",
    id: "medicine",
  },
  {
    title: "Accessories",
    id: "accessories",
  },
];

const StockIncompleteForm = () => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const fetchClinicList = useOrderManagementClinicListAPI();
  const routeIdOrder = getRouteKey("ORDER_MANAGEMENT", "id");
  const { routeState: routeClinicState } = useRouteInfo(routeIdOrder);
  const clinicsOrg = routeClinicState?.state?.clinicList?.results;
  const clinicsOrgItem = useSelectMenuReducer(clinicsOrg, "name", "id");
  const [isSubmit, setIsSubmit] = useState(false);
  const [isFinalSubmit, setIsFinalSubmit] = useState(false);
  const [itemSelected, setItemSelected] = useState({});
  const { newUserId, execute: postStockItem } = useStockItemsPostAPI();

  const searchParams = new URLSearchParams(window.location.search);
  const requesterName = searchParams.get("name_of_requester") || "";
  const clientName = searchParams.get("client_name") || "";
  const clientId = searchParams.get("client_id") || "";
  const userId = searchParams.get("unique_id") || "";

  const formHook = useForm<IInventoryStockIncompleteFormProps>({
    defaultValues: {
      requester_name: requesterName,
      client_name: clientName,
      client_id: clientId,
      items: [{ item_name_sub: "", quantity: +"", other: "", category: "" }],
      clinics: "",
      remarks: "",
    },
  });

  const { fields, append } = useFieldArray<IInventoryStockIncompleteFormProps>({
    control: formHook.control,
    name: "items",
  });

  useEffect(() => {
    fetchClinicList();
  }, []);

  useEffect(() => {
    if (newUserId) {
      navigate(getRouteKey("CITY_SETUP", "url"));
    }
  }, [newUserId]);

  useEffect(() => {
    formHook.setValue("date", format(new Date(), "yyyy-MM-dd"));
  }, [formHook]);

  const handleSelectVendor = (index: number, item: VendorSectionSearchResultType) => {
    formHook.setValue(`items.${index}.item_name_sub`, item.item_name);
    setItemSelected((prevState) => ({ ...prevState, [index]: !!item }));
  };

  const handleSelectItem = (index: number, item: VendorSectionSearchResultType) => {
    handleSelectVendor(index, item);
  };

  const handleSelectCustomItem = (index: number, value: string) => {
    // console.log(value, !!value);
    formHook.setValue(`items.${index}.other`, value);
    setItemSelected((prevState) => ({ ...prevState, [index]: !!value }));
  };

  // console.log(isSubmit, itemSelected, formHook.watch("items"));
  const Submit = (data: IInventoryStockIncompleteFormProps) => {
    setIsSubmit(true);
    if (data.items.some((item) => item.item_name_sub === "" || (item.item_name_sub === "Other" && item.other == ""))) {
      return;
    }
    if (data.items.some((item) => item.quantity == 0)) {
      data.items.map((item, index) => {
        if (item.quantity == 0) {
          formHook.setError(`items.${index}.quantity`, {
            type: "required",
            message: "Please select a quantity",
          });
        }
      });
      return;
    }
    const stockData: IStockIncompleteFormStockItemsPostBody = {
      client_id: data.client_id,
      client_name: data.client_name,
      clinic_name: data.clinics,
      item_name: formHook.getValues("items").map((item) => {
        return {
          item_name_sub: item.item_name_sub == "Other" ? item.other : item.item_name_sub,
          quantity: item.quantity,
          category: item.category,
        };
      }),
      name_of_requester: data.requester_name,
      remarks: data.remarks,
      user_id: userId,
      required_on: data.date,
      source: "FORM",
    };
    if (!isFinalSubmit) {
      postStockItem(stockData)
        .then(() => {
          toast.success("Form Submitted successfully!");
          // navigate(getRouteKey("FORM_TAB", "url"));
        })
        .catch((error) => {
          toast.error("Form Failed!" + error.message);
        });
    }
    setIsFinalSubmit(true);
  };

  return (
    <div className="m-3 p-4 rounded-md bg-white px-3 py-3">
      <h1 className="m-4 font-bold text-lg">Stock Incomplete Form</h1>
      <form onSubmit={formHook.handleSubmit(Submit)}>
        <div className="m-4">
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Requester Name</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <Input
              defaultValue={requesterName}
              register={formHook.register("requester_name", {
                required: true,
              })}
              fieldError={formHook.formState.errors.requester_name}
              errorMessages={[{ type: "required", message: "Requester Name is required" }]}
              className="h-9 w-[300px] text-sm rounded-md border-slate-400"
              placeholder="Enter Requester Name"
              readOnly
            />
          </div>
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Client Name</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <Input
              register={formHook.register("client_name", {
                required: true,
              })}
              fieldError={formHook.formState.errors.client_name}
              errorMessages={[{ type: "required", message: "Client Name is required" }]}
              className="h-9 w-[300px] text-sm rounded-md border-slate-400"
              placeholder="Enter Client Name"
              readOnly
            />
          </div>
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Client ID</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <Input
              register={formHook.register("client_id", {
                required: true,
              })}
              fieldError={formHook.formState.errors.client_id}
              errorMessages={[{ type: "required", message: "Client ID is required" }]}
              className="h-9 w-[300px] text-sm rounded-md border-slate-400"
              placeholder="Enter Client ID"
              readOnly
            />
          </div>
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Clinic</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <div className="w-[300px]">
              <SearchableSelectMenu
                selectItems={clinicsOrgItem}
                placeholder="Select Clinic"
                className="text-xs"
                showDropdownIcon={true}
                errorMessages={[{ type: "required", message: "Clinic is required" }]}
                fieldError={formHook.formState.errors.clinics}
                register={formHook.register("clinics", { required: true })}
                onSelectItem={(item) => {
                  console.log(item);
                  formHook.setValue("clinics", item.title);
                  formHook.clearErrors("clinics");
                }}
              />
            </div>
          </div>
          <div className="justify-start items-baseline flex mt-5 w-full">
            <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Required On</span>
            </div>
            <div className="w-[400px]">
              <PWLCalendarComponent
                showLeftNav={false}
                errorMessages={[]}
                register={formHook.register("date", {
                  required: true,
                })}
                showFutureYears={true}
                fieldError={formHook.formState.errors.date}
                onSelectDate={(date) => {
                  formHook.setValue("date", format(date, "yyyy-MM-dd"));
                }}
                className="w-full"
              />
            </div>
          </div>
          {fields.map((field, index) => (
            <div key={field.id}>
              <div className="justify-start flex mt-5">
                <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
                  <span className="text-black text-xs font-normal">Item {index + 1}</span>
                  <span className="text-rose-600 text-xs font-normal">*</span>
                </div>
                <div>
                  <StockItemListSearch
                    onSelectItem={(item) => handleSelectItem(index, item)}
                    clearSelectedItem={() => {
                      formHook.setValue(`items.${index}.item_name_sub`, "");
                      setItemSelected((prevState) => ({ ...prevState, [index]: false }));
                    }}
                    containerClassName={`w-[300px] ${isSubmit && !itemSelected[index] && "border border-red-500 text-red-500 rounded-md"}`}
                    itemActiveClassName="bg-orange-500 text-white"
                    inputContainerClassName=""
                    flexibleAdd={false}
                    placeholder="Search Item"
                    searchType="Lab"
                  />
                  {isSubmit && !itemSelected[index] && <p className="text-red-500">Item is required</p>}
                </div>
              </div>
              {formHook.watch(`items.${index}.item_name_sub`) === "Other" && (
                <>
                  <div className="justify-start items-center flex mt-5">
                    <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
                      <span className="text-black text-xs font-normal">Other Item {index + 1} Name</span>
                      <span className="text-rose-600 text-xs font-normal">*</span>
                    </div>
                    <div>
                      <Input
                        register={formHook.register(`items.${index}.other`)}
                        fieldError={formHook.formState.errors.items?.[index]?.other}
                        errorMessages={[]}
                        placeholder="Enter other item name"
                        onChange={(e) => handleSelectCustomItem(index, (e.target as HTMLInputElement).value)}
                        className="h-9 w-[300px] text-sm rounded-md border-slate-400 mt-2"
                      />
                      {isSubmit && formHook.watch(`items.${index}.other`) == "" && (
                        <p className="text-red-500">Other Item {index + 1} is required</p>
                      )}
                    </div>
                  </div>
                </>
              )}
              <div className="justify-start items-baseline flex mt-5">
                <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
                  <span className="text-black text-xs font-normal">Quantity</span>
                  <span className="text-rose-600 text-xs font-normal">*</span>
                </div>
                <Input
                  register={formHook.register(`items.${index}.quantity`)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (+e.target.value != 0) {
                      formHook.clearErrors(`items.${index}.quantity`);
                    } else {
                      formHook.setError(`items.${index}.quantity`, {
                        type: "required",
                        message: "Please select a quantity",
                      });
                    }
                  }}
                  type="number"
                  fieldError={formHook.formState.errors.items?.[index]?.quantity}
                  errorMessages={[{ type: "required", message: "Quantity" }]}
                  className="h-9 w-[300px] text-sm rounded-md border-slate-400"
                  placeholder="Enter Quantity"
                  min={0}
                  disabled={!itemSelected[index]}
                />
              </div>
              <div className="justify-start items-baseline flex mt-5">
                <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
                  <span className="text-black text-xs font-normal">Category</span>
                </div>
                <div className="w-[300px]">
                  <SearchableSelectMenu
                    selectItems={CategoryList}
                    placeholder="Select Category"
                    className="text-xs"
                    showDropdownIcon={true}
                    errorMessages={[]}
                    fieldError={formHook.formState.errors.items?.[index]?.category}
                    register={formHook.register(`items.${index}.category`)}
                    onSelectItem={(item) => {
                      formHook.setValue(`items.${index}.category`, item.title);
                      formHook.clearErrors(`items.${index}.category`);
                    }}
                  />
                </div>
              </div>
            </div>
          ))}

          <button
            onClick={(e) => {
              e.preventDefault();
              append({ item_name_sub: "", quantity: 0, other: "", category: "" });
            }}
            className="rounded-md bg-[#193A6A] px-3 mx-3 mt-5 py-1.5 font-normal cursor-pointer text-white text-sm"
          >
            Add More
          </button>

          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Remarks</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <textarea
              id="notes"
              rows={4}
              value={formHook.watch("remarks")}
              required
              onChange={(e) => formHook.setValue("remarks", e.target.value)}
              className=" p-2.5 mt-3 w-[300px] text-xs bg-white text-black-900 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Remarks"
            ></textarea>
          </div>
          <div className="bottom-0 mt-5 flex justify-end p-4 gap-4">
            <button
              type="submit"
              className={`rounded-md ${isFinalSubmit ? "bg-[grey] cursor-not-allowed" : "bg-[#193A6A]"} px-3 py-1.5 font-normal cursor-pointer text-white text-sm`}
              disabled={isFinalSubmit}
              value={"Submit"}
            >
              Submit
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default StockIncompleteForm;
